import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import StripeSettingsTab from '@/components/settings/StripeSettingsTab';
import TeyaStoreSettingsTab from '@/components/settings/TeyaStoreSettingsTab';
import DojoStoreSettingsTab from '@/components/settings/DojoStoreSettingsTab';
import { cardTerminalApi, storefrontAdminApi, stripeApi } from '@/services/api';
import { getApiErrorMessage } from '@/utils/apiError';

export type StoreCardProvider = 'none' | 'stripe' | 'teya' | 'dojo';

const PROVIDERS: {
  id: StoreCardProvider;
  title: string;
  desc: string;
}[] = [
  { id: 'none', title: 'None', desc: 'COD / pay on collection or delivery only' },
  { id: 'stripe', title: 'Stripe', desc: 'Hosted or embedded card checkout' },
  { id: 'teya', title: 'Teya', desc: 'Teya Hosted Checkout' },
  { id: 'dojo', title: 'Dojo', desc: 'Dojo Prebuilt Checkout' },
];

/**
 * Single Public Store “Card payment” page: pick one gateway at a time, then configure it.
 */
export default function StoreCardPaymentTab() {
  const qc = useQueryClient();
  const [provider, setProvider] = useState<StoreCardProvider | null>(null);

  const { data: store, isLoading: storeLoading } = useQuery({
    queryKey: ['storefrontAdminSettings'],
    queryFn: async () => (await storefrontAdminApi.getSettings()).data.message,
  });

  const { data: stripe } = useQuery({
    queryKey: ['tenantStripeSettings'],
    queryFn: async () => (await stripeApi.getTenantSettings()).data.message,
  });

  const { data: teya } = useQuery({
    queryKey: ['teyaSettings'],
    queryFn: async () => (await cardTerminalApi.getTeyaSettings()).data.message,
  });

  const { data: dojo } = useQuery({
    queryKey: ['dojoSettings'],
    queryFn: async () => (await cardTerminalApi.getDojoSettings()).data.message,
  });

  const inferred = useMemo<StoreCardProvider>(() => {
    if (Number(store?.pay_online_dojo) === 1) return 'dojo';
    if (Number(store?.pay_online_teya) === 1) return 'teya';
    if (Number(store?.pay_online_stripe) === 1) return 'stripe';
    return 'none';
  }, [store?.pay_online_stripe, store?.pay_online_teya, store?.pay_online_dojo]);

  useEffect(() => {
    if (provider === null && store) setProvider(inferred);
  }, [store, inferred, provider]);

  const active = provider ?? inferred;

  const selectProvider = useMutation({
    mutationFn: async (next: StoreCardProvider) => {
      // 1) Storefront flags are source of truth for which gateway the shop offers
      const base = { ...(store || {}) };
      await storefrontAdminApi.saveSettings({
        ...base,
        pay_online_stripe: next === 'stripe' ? 1 : 0,
        pay_online_teya: next === 'teya' ? 1 : 0,
        pay_online_dojo: next === 'dojo' ? 1 : 0,
      });

      const stripeBase = {
        mode: stripe?.mode || 'Sandbox',
        checkout_type: stripe?.checkout_type || 'redirect',
        currency: stripe?.currency || 'GBP',
        statement_descriptor: stripe?.statement_descriptor || '',
        auto_complete_order: stripe?.auto_complete_order !== 0,
        test_publishable_key: stripe?.test_publishable_key || '',
        live_publishable_key: stripe?.live_publishable_key || '',
      };

      // 2) Best-effort align provider docs — never block switching if credentials are incomplete
      const soft = async (label: string, fn: () => Promise<unknown>) => {
        try {
          await fn();
        } catch (e) {
          console.warn(`[Card payment] ${label} sync failed`, e);
        }
      };

      await soft('stripe', () =>
        stripeApi.saveTenantSettings({
          ...stripeBase,
          enabled: next === 'stripe' ? 1 : 0,
        } as any),
      );
      await soft('teya', () =>
        cardTerminalApi.saveTeyaSettings({
          enabled: next === 'teya' ? 1 : teya?.enabled ? 1 : 0,
          enable_hosted_checkout: next === 'teya' ? 1 : 0,
          // Keep storefront flag aligned (exclusive gateway)
          sync_storefront: 1,
          mode: teya?.mode || 'Sandbox',
          currency: teya?.currency || 'GBP',
          client_id: teya?.client_id || '',
          store_id: teya?.store_id || '',
          enable_pay_by_link: teya?.enable_pay_by_link !== 0 ? 1 : 0,
        }),
      );
      await soft('dojo', () =>
        cardTerminalApi.saveDojoSettings({
          enabled: next === 'dojo' ? 1 : dojo?.enabled ? 1 : 0,
          enable_hosted_checkout: next === 'dojo' ? 1 : 0,
          sync_storefront: 1,
          currency: dojo?.currency || 'GBP',
          terminal_id: dojo?.terminal_id || '',
          software_house_id: dojo?.software_house_id || '',
          reseller_id: dojo?.reseller_id || '',
        }),
      );
    },
    onSuccess: (_d, next) => {
      setProvider(next);
      void qc.invalidateQueries({ queryKey: ['storefrontAdminSettings'] });
      void qc.invalidateQueries({ queryKey: ['tenantStripeSettings'] });
      void qc.invalidateQueries({ queryKey: ['teyaSettings'] });
      void qc.invalidateQueries({ queryKey: ['dojoSettings'] });
      toast.success(
        next === 'none'
          ? 'Online card payment turned off'
          : `${next.charAt(0).toUpperCase() + next.slice(1)} selected for the shop`,
      );
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const status = useMemo(() => {
    if (active === 'stripe') {
      const ready = Boolean(stripe?.enabled) && (Boolean(stripe?.has_test_secret_key) || Boolean(stripe?.has_live_secret_key) || Boolean(stripe?.test_publishable_key));
      return ready
        ? { ok: true, text: 'Stripe is active for public store checkout' }
        : { ok: false, text: 'Stripe selected — finish API keys below' };
    }
    if (active === 'teya') {
      const ready = Boolean(teya?.ready_hosted_checkout || (teya?.enabled && teya?.has_client_secret && teya?.client_id));
      return ready
        ? { ok: true, text: 'Teya is active for public store checkout' }
        : { ok: false, text: 'Teya selected — finish Client ID / Secret below' };
    }
    if (active === 'dojo') {
      const ready = Boolean(dojo?.ready_hosted_checkout || (dojo?.enabled && dojo?.has_api_key && dojo?.enable_hosted_checkout));
      return ready
        ? { ok: true, text: 'Dojo is active for public store checkout' }
        : { ok: false, text: 'Dojo selected — finish API key below' };
    }
    return { ok: true, text: 'No online card gateway — customers pay on collection / delivery' };
  }, [active, stripe, teya, dojo]);

  if (storeLoading || provider === null) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading card payment settings…
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-slate-900 text-white p-2.5">
            <CreditCard className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-900">Card payment</h2>
            <p className="text-sm text-slate-600 mt-1">
              Choose <strong>one</strong> online card gateway for your public store. Only the selected
              provider is offered at checkout. POS terminals stay under{' '}
              <Link to="/settings?tab=card_machines" className="underline font-medium text-slate-800">
                Settings → Card machines
              </Link>
              .
            </p>
          </div>
        </div>
        <div
          className={`mt-4 flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm ${
            status.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-900'
          }`}
        >
          {status.ok ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
          {status.text}
        </div>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Step 1</div>
          <h3 className="text-base font-semibold text-slate-900 mt-0.5">Active gateway</h3>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {PROVIDERS.map((opt) => {
            const selected = active === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                disabled={selectProvider.isPending}
                onClick={() => {
                  if (opt.id === active) return;
                  selectProvider.mutate(opt.id);
                }}
                className={`text-left rounded-xl border px-4 py-3 transition disabled:opacity-60 ${
                  selected
                    ? 'border-slate-900 bg-slate-900 text-white shadow'
                    : 'border-slate-200 hover:border-slate-400 bg-white'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex h-4 w-4 rounded-full border-2 items-center justify-center ${
                      selected ? 'border-white' : 'border-slate-400'
                    }`}
                  >
                    {selected ? <span className="h-2 w-2 rounded-full bg-white" /> : null}
                  </span>
                  <span className="font-semibold">{opt.title}</span>
                </div>
                <div className={`text-xs mt-1.5 ${selected ? 'text-slate-300' : 'text-slate-500'}`}>
                  {opt.desc}
                </div>
              </button>
            );
          })}
        </div>
        {selectProvider.isPending && (
          <p className="text-xs text-slate-500 inline-flex items-center gap-1.5">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Updating active gateway…
          </p>
        )}
      </section>

      {active !== 'none' && (
        <section className="space-y-3">
          <div className="px-1">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Step 2</div>
            <h3 className="text-base font-semibold text-slate-900 mt-0.5">
              {active === 'stripe' ? 'Stripe' : active === 'teya' ? 'Teya' : 'Dojo'} configuration
            </h3>
          </div>

          {active === 'stripe' && <StripeSettingsTab embedded />}
          {active === 'teya' && <TeyaStoreSettingsTab embedded />}
          {active === 'dojo' && <DojoStoreSettingsTab embedded />}
        </section>
      )}
    </div>
  );
}
